/**
 * @file Shows the back office user that a public key's ID token acts as.
 */

import React from "react";
import { Tag, Space, Typography } from "antd";
import { useOutletContext } from "react-router-dom";

import { W0 } from "@components/Constants";
import { ROLES_MAP } from "@components/Domain";

const LABEL_TITLE = "The key acts as this user";
const LABEL_UNKNOWN = "The user of this key is not in the organization anymore. The key authenticates no client while that holds.";

type User = {
  id: string;
  email: string;
  roles: string[];
  givenName?: string;
  familyName?: string;
  isDisabled?: boolean;
};

type OutletContext = {
  users?: User[];
};

type Role = {
  title: string;
  tagColor: string;
};

type Props = {
  userId: string;
};

/**
 * Looks up the display title and tag color of a role by its ID.
 */
const getRole = (roleId: string): Role | undefined => {
  const rolesMap = ROLES_MAP as Record<string, Role | undefined>;
  const role = rolesMap[roleId];

  return role;
};

/**
 * Renders the name, email and roles of the key's user, or a warning when that user is gone.
 */
const KeyUser = ({ userId }: Props): JSX.Element => {
  const { users } = useOutletContext<OutletContext>();

  const labelStyle = {
    marginBottom: W0 / 2,
  };

  const user = (users || []).find(({ id }) => id === userId);

  const hasUser = !!user;

  if (!hasUser) {
    return (
      <>
        <Typography.Paragraph type="secondary" style={labelStyle}>
          {LABEL_TITLE}
        </Typography.Paragraph>

        <Typography.Paragraph type="warning">
          {LABEL_UNKNOWN}
        </Typography.Paragraph>
      </>
    );
  }

  const { email, roles, givenName, familyName } = user;

  const fullName = `${givenName} ${familyName}`;

  /**
   * Renders one role as a colored tag.
   */
  const renderRole = (roleId: string) => {
    const role = getRole(roleId);

    if (!role) {
      return <></>;
    }

    const { title, tagColor } = role;

    return (
      <Tag key={roleId} color={tagColor}>
        {title}
      </Tag>
    );
  };

  return (
    <>
      <Typography.Paragraph type="secondary" style={labelStyle}>
        {LABEL_TITLE}
      </Typography.Paragraph>

      <Space size={W0} wrap>
        <Typography.Text strong>{fullName}</Typography.Text>

        <Typography.Text type="secondary">{email}</Typography.Text>

        <Space size={0} wrap>
          {roles.map(renderRole)}
        </Space>
      </Space>
    </>
  );
};

export default KeyUser;
